import { useState, useEffect, useCallback, useMemo } from 'react'
import { useContext } from 'react'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getMPDataWithParams } from 'src/mentor/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import {
  checkIntoRoomWithLocalStorageApplied,
  peekIntoRoom,
  checkOutRoom,
} from 'src/clinic/components/CheckIn/services'
import { toast } from 'react-toastify'
import {
  getActivePeekIns,
  getPeekInForClinic,
  addOrUpdatePeekIn,
  removePeekIn,
} from '../utils/peekInManager'

/**
 * Hook for page-level check-in guard
 * Checks if user needs to check-in for any clinic before accessing the page
 * @param {Object} options - Configuration options
 * @param {boolean} options.requireCheckIn - Whether to require check-in (default: false, allows peek-in)
 * @returns {Object} Guard state and functions
 */
export function usePageCheckInGuard({ requireCheckIn = false } = {}) {
  const mentorProfileContext = useContext(MentorProfileContext)
  const { mentorProfileData = null, mentorClinicAssociationData = null } = mentorProfileContext || {}
  const [checkInPromptOpen, setCheckInPromptOpen] = useState(false)
  const [checkingIn, setCheckingIn] = useState(false)
  const [availableRooms, setAvailableRooms] = useState([])
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [selectedClinic, setSelectedClinic] = useState(null)
  const [actionType, setActionType] = useState(null) // 'checkin' or 'peekin'
  const [selectedCheckInMethod, setSelectedCheckInMethod] = useState(null)

  // Check if user is checked in or peeked in to any clinic
  const isCheckedInOrPeekedIn = useMemo(() => {
    const checkedInDetails = localStorage.getItem('checkedInDetails')
    const activePeekIns = getActivePeekIns()

    // Check if checked in to any clinic
    if (checkedInDetails) {
      try {
        const checkedIn = JSON.parse(checkedInDetails)
        if (checkedIn?.clinicLocationId) {
          return { isCheckedIn: true, isPeekedIn: false, details: checkedIn }
        }
      } catch (error) {
        console.error('Error parsing checked-in details:', error)
      }
    }

    // Check if peeked in to any clinic
    if (activePeekIns.length > 0) {
      return { isCheckedIn: false, isPeekedIn: true, details: activePeekIns[0] }
    }

    return false
  }, [])

  // Get clinic data from check-in/peek-in status
  const currentClinicData = useMemo(() => {
    if (!isCheckedInOrPeekedIn || !mentorClinicAssociationData) return null
    
    const clinicLocationId = isCheckedInOrPeekedIn.details?.clinicLocationId
    if (!clinicLocationId) return null

    return mentorClinicAssociationData.find((clinic) => clinic.id === clinicLocationId)
  }, [isCheckedInOrPeekedIn, mentorClinicAssociationData])

  // Get clinic brand data
  const clinicBrandData = useMemo(() => {
    if (!currentClinicData) return null

    return {
      id: currentClinicData.clinicBrandId,
      name: currentClinicData.clinicBrandName || 
            currentClinicData.clinicBrandData?.brandName || 
            currentClinicData.clinicBrandData?.title ||
            'Unknown Brand',
    }
  }, [currentClinicData])

  // Fetch available rooms for a clinic
  const fetchAvailableRooms = useCallback(async (clinicLocationId) => {
    if (!clinicLocationId) return

    try {
      const response = await getMPDataWithParams(`/api/secure/teacher/associated-chamber-rooms`, {
        clinicLocationId: clinicLocationId,
      })

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        const rooms = response?.data?.data?.['defined-room-access'] || []
        setAvailableRooms(rooms)
        // Auto-select first available room
        if (rooms.length > 0) {
          setSelectedRoom(rooms[0])
        } else {
          setSelectedRoom(null)
        }
      }
    } catch (error) {
      console.error('Error fetching rooms:', error)
      toast.error('Failed to fetch available rooms')
      setAvailableRooms([])
      setSelectedRoom(null)
    }
  }, [])

  // Handle check-in
  const handleCheckIn = useCallback(async (checkInMethod = null) => {
    if (!selectedRoom || !selectedClinic) {
      toast.error('Please select a room and clinic')
      return false
    }

    // Check if user is checked in to a different clinic and checkout first
    const existingCheckedIn = localStorage.getItem('checkedInDetails')
    if (existingCheckedIn) {
      try {
        const checkedInDetails = JSON.parse(existingCheckedIn)
        if (checkedInDetails?.clinicLocationId && checkedInDetails.clinicLocationId !== selectedClinic.id) {
          await checkOutRoom(checkedInDetails)
          localStorage.removeItem('checkedInDetails')
          localStorage.removeItem('peekedInRoomDetails')
          localStorage.removeItem('roomDataEventTypes')
          toast.info('Checked out from previous clinic')
        }
      } catch (error) {
        console.error('Error checking out from previous clinic:', error)
      }
    }

    // Determine check-in method
    let method = checkInMethod
    if (!method && selectedRoom?.checkInMethodOptions?.length > 0) {
      const firstEnabled = selectedRoom.checkInMethodOptions.find((m) => m?.enabled !== false)
      method = firstEnabled?.id
    }
    if (!method) {
      method = 'check-in-method-in-person'
    }

    setCheckingIn(true)
    try {
      const roomData = {
        ...selectedRoom,
        clinicLocationId: selectedClinic.id,
        clinicBrandId: selectedClinic.clinicBrandId,
        checkinMethod: method,
      }

      await checkIntoRoomWithLocalStorageApplied(roomData)
      toast.success('Successfully checked in')
      setCheckInPromptOpen(false)
      return true
    } catch (error) {
      console.error('Error checking in:', error)
      toast.error('Failed to check in')
      return false
    } finally {
      setCheckingIn(false)
    }
  }, [selectedRoom, selectedClinic])

  // Handle peek-in
  const handlePeekIn = useCallback(async () => {
    if (!selectedRoom || !selectedClinic) {
      toast.error('Please select a room and clinic')
      return false
    }

    setCheckingIn(true)
    try {
      const roomData = {
        ...selectedRoom,
        clinicLocationId: selectedClinic.id,
        clinicBrandId: selectedClinic.clinicBrandId,
      }

      const response = await peekIntoRoom(roomData)
      
      if (response?.status === 200) {
        // Use new peek-in manager with expiration
        addOrUpdatePeekIn(roomData)
        toast.success('Successfully peeked in')
        setCheckInPromptOpen(false)
        return true
      } else {
        toast.error('Failed to peek in')
        return false
      }
    } catch (error) {
      console.error('Error peeking in:', error)
      toast.error('Failed to peek in')
      return false
    } finally {
      setCheckingIn(false)
    }
  }, [selectedRoom, selectedClinic])

  // Initialize check-in prompt
  const initializeCheckInPrompt = useCallback(async (clinicLocationId) => {
    if (!clinicLocationId || !mentorClinicAssociationData) return

    const clinic = mentorClinicAssociationData.find((c) => c.id === clinicLocationId)
    if (!clinic) {
      toast.error('Clinic not found')
      return
    }

    setSelectedClinic(clinic)
    await fetchAvailableRooms(clinicLocationId)
    setCheckInPromptOpen(true)
  }, [mentorClinicAssociationData, fetchAvailableRooms])

  // Check if page access should be blocked
  const shouldBlockAccess = useMemo(() => {
    // If already checked in or peeked in, allow access
    if (isCheckedInOrPeekedIn) return false
    
    // If requireCheckIn is true, block access until check-in
    // Otherwise, allow access but show prompt
    return requireCheckIn
  }, [isCheckedInOrPeekedIn, requireCheckIn])

  // Reset state when dialog closes
  useEffect(() => {
    if (!checkInPromptOpen) {
      setActionType(null)
      setSelectedCheckInMethod(null)
      setSelectedRoom(null)
    }
  }, [checkInPromptOpen])

  // Get available clinics
  const availableClinics = useMemo(() => {
    return mentorClinicAssociationData || []
  }, [mentorClinicAssociationData])

  // Handle clinic change
  const handleClinicChange = useCallback(async (clinic) => {
    setSelectedClinic(clinic)
    setSelectedRoom(null)
    setActionType(null)
    setSelectedCheckInMethod(null)
    if (clinic?.id) {
      await fetchAvailableRooms(clinic.id)
    }
  }, [fetchAvailableRooms])

  return {
    // State
    checkInPromptOpen,
    checkingIn,
    availableRooms,
    selectedRoom,
    selectedClinic,
    actionType,
    selectedCheckInMethod,
    isCheckedInOrPeekedIn,
    currentClinicData,
    clinicBrandData,
    shouldBlockAccess,
    availableClinics,

    // Actions
    setCheckInPromptOpen,
    setSelectedRoom,
    setActionType,
    setSelectedCheckInMethod,
    handleCheckIn,
    handlePeekIn,
    initializeCheckInPrompt,
    handleClinicChange,
  }
}

